/**
 * `wayai pull` / `wayai push` against the `communities/` subtree (gh #5003): a community's
 * settings, rules, join link state and rooms as files — `communities/<slug>/community.yaml` and
 * `rules.md` — synced three-way per entity (`model.ts`, the Sync rules table).
 *
 * There is no preview: a community is one live store, so push edits what members see. Push
 * always prints what it will change and asks first (`-y` skips; with no terminal and no `-y` it
 * refuses), never creates a community and never deletes anything — a room missing from the files
 * is left as is, and `archived: true` is how a room leaves. Every request of a run, reads and
 * writes, goes through one `Pacer`. Each write's id and rev are written back to the files as soon
 * as it returns, so a push stopped anywhere leaves every rev true to the server and its rerun
 * converges.
 *
 * Courses are not synced yet: `community.yaml` holds no course, and push leaves every course as is.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import {
  COMMUNITY_ROOMS_MAX,
  type CommunityJoinLink,
  type CommunityRoom,
  type CommunityRoomUpdateBody,
  type CommunitySettings,
  type CommunitySettingsBody,
  type CommunitySummary,
} from '@wayai/core/contracts';
import type { ApiClient } from '../lib/api-client.js';
import { extractApiMessage } from '../lib/errors.js';
import { expected } from '../lib/expected.js';
import { requireRealSubdirNoSymlink } from '../lib/fs-safety.js';
import { communitiesDirLabel, resolveLayout, WAYAI_LAYOUT } from '../lib/layout.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import { confirm } from '../lib/utils.js';
import { WORKSPACE_MANIFEST_LABEL } from '../lib/workspace-manifest.js';
import { assertInScope, seedScopeIfEmpty } from '../lib/worktree-scope.js';
import { COMMUNITY_FILE, resolveCommunityTarget } from '../lib/community-workspace.js';
import { isInteractive } from '../data/helpers.js';
import { callCommunities, communitiesClient, communityNotFound, orgNotFound } from './api.js';
import { COMMUNITY_REQUESTS_PER_MINUTE, Pacer, paceDuration } from './pacer.js';
import { readCommunityFolder, writeCommunityFolder, type CommunityFiles, type RoomEntry } from './files.js';
import {
  classify,
  createClientId,
  joinLinkRev,
  localName,
  roomContent,
  roomRev,
  serverRoom,
  serverSettings,
  settingsContent,
  settingsRev,
  type SettingsContent,
  type SyncState,
} from './model.js';

interface SyncArgs {
  autoConfirm: boolean;
  dryRun: boolean;
  theirs: boolean;
}

/** The flags this side reads. The target — the positional or `--community` — is the router's. */
function parseArgs(args: string[]): SyncArgs {
  return {
    autoConfirm: args.includes('--yes') || args.includes('-y'),
    dryRun: args.includes('--dry-run'),
    theirs: args.includes('--theirs'),
  };
}

function requireGitRoot(gitRoot: string | null): string {
  if (!gitRoot) throw expected('Not inside a git repository.');
  return gitRoot;
}

/** Server text (and file text) as printed for a person. */
const show = (text: string) => JSON.stringify(sanitizeTerminalText(text));

// ── The server side: one community, read and written through the pacer ──

interface ServerState {
  community: CommunitySummary;
  settings: CommunitySettings;
  rooms: CommunityRoom[];
  joinLink: CommunityJoinLink;
}

class Remote {
  constructor(
    private readonly api: ApiClient,
    private readonly pacer: Pacer,
    readonly communityId: string,
  ) {}

  private async send<T>(request: () => Promise<{ data: T }>, notFound = communityNotFound(this.communityId)): Promise<T> {
    return (await callCommunities(this.pacer.run(request), notFound)).data;
  }

  /**
   * The four reads (gh #5003 Decision 8's bounded read, courses aside). The org's list both binds
   * the community to the workspace's organization and names it.
   */
  async read(orgId: string): Promise<ServerState> {
    const communities = await this.send(() => this.api.listOrgCommunities(orgId), orgNotFound(orgId));
    const community = communities.find((c) => c.community_id === this.communityId);
    if (!community) {
      throw expected(
        `Community ${this.communityId} is not one of the communities of organization ${orgId}, the one ${WORKSPACE_MANIFEST_LABEL} names. ` +
          'A community is pulled and pushed from a workspace bound to the organization that owns it.',
      );
    }
    // The org's list first, so its refusal speaks before a bare 404; the rest do not depend on each other.
    const [settings, rooms, joinLink] = await Promise.all([
      this.send(() => this.api.getCommunitySettings(this.communityId)),
      this.send(() => this.api.listCommunityRooms(this.communityId)),
      this.send(() => this.api.getCommunityJoinLink(this.communityId)),
    ]);
    return { community, settings, rooms, joinLink };
  }

  updateSettings(body: CommunitySettingsBody): Promise<CommunitySettings> {
    return this.send(() => this.api.updateCommunitySettings(this.communityId, body));
  }

  setJoinLink(enabled: boolean): Promise<CommunityJoinLink> {
    return this.send(() =>
      enabled ? this.api.replaceCommunityJoinLink(this.communityId) : this.api.disableCommunityJoinLink(this.communityId));
  }

  async createRoom(entry: RoomEntry): Promise<CommunityRoom> {
    const client_id = await createClientId(this.communityId, 'room', entry.key);
    return this.send(() => this.api.createCommunityRoom(this.communityId, { kind: entry.kind, name: entry.name, client_id }));
  }

  updateRoom(roomId: string, body: CommunityRoomUpdateBody): Promise<CommunityRoom> {
    return this.send(() => this.api.updateCommunityRoom(this.communityId, roomId, body));
  }
}

async function openRemote(communityId: string): Promise<Remote> {
  const pacer = new Pacer({
    onWait: (ms, reason) =>
      console.error(
        reason === 'paced'
          ? `  Pacing at ${COMMUNITY_REQUESTS_PER_MINUTE} requests a minute: waiting ${Math.ceil(ms / 1000)} s.`
          : `  The community request budget is spent (your web console and messages share it): waiting ${Math.ceil(ms / 1000)} s.`,
      ),
  });
  return new Remote(await communitiesClient(), pacer, communityId);
}

// ── The plan: every entity's verdict ──

interface SettingsPlan {
  state: SyncState;
  file: SettingsContent;
  server: SettingsContent;
  serverRev: string;
}

interface JoinLinkPlan {
  state: SyncState;
  file: boolean;
  server: boolean;
  serverRev: string;
}

type RoomPlan =
  | { kind: 'create'; entry: RoomEntry }
  | { kind: 'existing'; entry: RoomEntry; server: CommunityRoom; state: SyncState; serverRev: string };

interface Plan {
  settings: SettingsPlan;
  joinLink: JoinLinkPlan;
  rooms: RoomPlan[];
  /** On the server, not in the files: left as is by push, added to the files by pull. */
  untracked: CommunityRoom[];
  /** In the files with an id the server does not have: refused by both verbs. */
  unknownIds: RoomEntry[];
}

function fileSettingsContent(files: CommunityFiles): SettingsContent {
  return settingsContent({ rules: files.rules, ...files.settings });
}

function planSync(files: CommunityFiles, server: ServerState): Plan {
  const id = files.community_id;
  const fileSettings = fileSettingsContent(files);
  const settingsSide = serverSettings(id, server.settings);
  const joinLinkServerRev = joinLinkRev(id, { enabled: server.joinLink.enabled });
  const byId = new Map(server.rooms.map((room) => [room.room_id, room]));
  const rooms: RoomPlan[] = [];
  const unknownIds: RoomEntry[] = [];
  for (const entry of files.rooms) {
    if (entry.id === undefined) {
      rooms.push({ kind: 'create', entry });
      continue;
    }
    const room = byId.get(entry.id);
    if (!room) {
      unknownIds.push(entry);
      continue;
    }
    const roomSide = serverRoom(room);
    const state = classify(entry.rev, roomRev(room.room_id, roomContent(entry)), roomSide.rev);
    rooms.push({ kind: 'existing', entry, server: room, state, serverRev: roomSide.rev });
  }
  const tracked = new Set(files.rooms.map((room) => room.id));
  return {
    settings: {
      state: classify(files.settings.rev, settingsRev(id, fileSettings), settingsSide.rev),
      file: fileSettings,
      server: settingsSide.content,
      serverRev: settingsSide.rev,
    },
    joinLink: {
      state: classify(files.join_link_rev, joinLinkRev(id, { enabled: files.join_link_enabled }), joinLinkServerRev),
      file: files.join_link_enabled,
      server: server.joinLink.enabled,
      serverRev: joinLinkServerRev,
    },
    rooms,
    untracked: server.rooms.filter((room) => !tracked.has(room.room_id)),
    unknownIds,
  };
}

function unknownIdProblems(plan: Plan): string[] {
  return plan.unknownIds.map((entry) => `room ${entry.key}: ${entry.id} is not a room of this community`);
}

// ── Rendering ──

function settingsChanges(from: SettingsContent, to: SettingsContent): string[] {
  const out: string[] = [];
  if (from.slow_mode_seconds !== to.slow_mode_seconds) out.push(`slow_mode_seconds ${from.slow_mode_seconds} → ${to.slow_mode_seconds}`);
  if (from.courses_label !== to.courses_label) out.push(`courses_label ${show(from.courses_label)} → ${show(to.courses_label)}`);
  if (from.rules !== to.rules) out.push('rules (rules.md)');
  return out;
}

/** The lines that change between two texts, `-` then `+`; a whole replacement past a size bound. */
function lineDiff(from: string | null, to: string | null): string[] {
  const a = from ? sanitizeTerminalText(from).split('\n') : [];
  const b = to ? sanitizeTerminalText(to).split('\n') : [];
  if (a.length * b.length > 250_000) return [...a.map((l) => `- ${l}`), ...b.map((l) => `+ ${l}`)];
  const lcs = Array.from({ length: a.length + 1 }, () => new Array<number>(b.length + 1).fill(0));
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
    }
  }
  const out: string[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      i++;
      j++;
    } else if (j >= b.length || (i < a.length && lcs[i + 1][j] >= lcs[i][j + 1])) {
      out.push(`- ${a[i++]}`);
    } else {
      out.push(`+ ${b[j++]}`);
    }
  }
  return out;
}

function roomChanges(from: { name: string; archived: boolean }, to: { name: string; archived: boolean }): string[] {
  const out: string[] = [];
  if (from.name !== to.name) out.push(`name ${show(from.name)} → ${show(to.name)}`);
  if (from.archived !== to.archived) out.push(to.archived ? 'archived' : 'brought back');
  return out;
}

const onOff = (enabled: boolean) => (enabled ? 'on' : 'off');

function joinLinkChange(from: boolean, to: boolean): string {
  return to
    ? `${onOff(from)} → on: a new link — every link shared before stays dead`
    : `${onOff(from)} → off: every link shared before stops working`;
}

/** What one direction would change, as printed lines. Shared by push (server → files) and pull (files → server). */
interface Listing {
  changes: string[];
  serverChanged: string[];
  conflicts: string[];
  leftAsIs: string[];
  kept: string[];
}

function printSection(title: string, lines: string[]): void {
  if (lines.length === 0) return;
  console.log(`\n${title}`);
  for (const line of lines) console.log(`  ${line}`);
}

function printListing(listing: Listing): void {
  printSection('Changes:', listing.changes);
  printSection('Changed on the server since your last pull — left as is; pull to take it:', listing.serverChanged);
  printSection('Kept — edited in your files, not pushed yet:', listing.kept);
  printSection('Not in your files — left as is:', listing.leftAsIs);
  printSection('Conflicts — changed in your files and on the server since your last pull:', listing.conflicts);
}

// ── push ──

interface Write {
  label: string;
  /** Requests it makes, for the duration stated before the question. */
  requests: number;
  apply: () => Promise<void>;
}

/** Refusals from the read, before any write: unknown ids, read-only fields, the room cap. */
function pushProblems(files: CommunityFiles, server: ServerState, plan: Plan): string[] {
  const problems = unknownIdProblems(plan);
  if (files.name !== server.community.name) {
    problems.push(`name: a community's name cannot change from the files (it is ${show(server.community.name)})`);
  }
  for (const room of plan.rooms) {
    if (room.kind === 'existing' && room.entry.kind !== room.server.kind) {
      problems.push(`room ${room.entry.key}: kind cannot change once a room exists (it is ${room.server.kind})`);
    }
  }
  // A create may be the retry of one whose answer was lost, which the server answers with the room
  // it made — one of the rooms the files do not list. Only the creates no such room can explain
  // are certainly new.
  const creates = plan.rooms.filter((room) => room.kind === 'create').length;
  const certainlyNew = Math.max(0, creates - plan.untracked.length);
  if (server.rooms.length + certainlyNew > COMMUNITY_ROOMS_MAX) {
    problems.push(
      `rooms: a community holds at most ${COMMUNITY_ROOMS_MAX} rooms, archived ones included; it has ${server.rooms.length}, and the files create ${creates}`,
    );
  }
  return problems;
}

function settingsPatch(files: CommunityFiles, server: SettingsContent): CommunitySettingsBody {
  const file = fileSettingsContent(files);
  return {
    // Raw: the text as the file holds it; the server trims it and reads '' as none.
    ...(file.rules !== server.rules ? { rules: files.rules } : {}),
    ...(file.slow_mode_seconds !== server.slow_mode_seconds ? { slow_mode_seconds: files.settings.slow_mode_seconds } : {}),
    ...(file.courses_label !== server.courses_label ? { courses_label: files.settings.courses_label } : {}),
  };
}

function roomPatch(entry: RoomEntry, room: CommunityRoom): CommunityRoomUpdateBody | null {
  const file = roomContent(entry);
  const server = roomContent(room);
  const body: CommunityRoomUpdateBody = {
    ...(file.name !== server.name ? { name: entry.name } : {}),
    ...(file.archived !== server.archived ? { archived: entry.archived } : {}),
  };
  return Object.keys(body).length > 0 ? body : null;
}

/** Record a server room's answer in its entry: content, id and rev. */
function takeRoom(entry: RoomEntry, room: CommunityRoom): void {
  entry.id = room.room_id;
  entry.rev = serverRoom(room).rev;
  entry.name = room.name;
  entry.archived = room.archived;
  entry.kind = room.kind;
}

function takeSettings(files: CommunityFiles, settings: CommunitySettings): void {
  files.settings = {
    rev: serverSettings(files.community_id, settings).rev,
    slow_mode_seconds: settings.slow_mode_seconds,
    courses_label: settings.courses_label ?? '',
  };
  files.rules = settings.rules ?? '';
}

function takeJoinLink(files: CommunityFiles, enabled: boolean): void {
  files.join_link_enabled = enabled;
  files.join_link_rev = joinLinkRev(files.community_id, { enabled });
}

/** The writes a push makes, in the apply order, each writing its id and rev back once it returns. */
function pushWrites(files: CommunityFiles, plan: Plan, remote: Remote, save: () => void): Write[] {
  const writes: Write[] = [];
  if (plan.settings.state === 'local') {
    const body = settingsPatch(files, plan.settings.server);
    writes.push({
      label: 'settings',
      requests: 1,
      apply: async () => {
        takeSettings(files, await remote.updateSettings(body));
        save();
      },
    });
  }
  if (plan.joinLink.state === 'local') {
    const enabled = plan.joinLink.file;
    writes.push({
      label: `join link ${onOff(enabled)}`,
      requests: 1,
      apply: async () => {
        takeJoinLink(files, (await remote.setJoinLink(enabled)).enabled);
        save();
      },
    });
  }
  for (const room of plan.rooms) {
    if (room.kind !== 'create') continue;
    const { entry } = room;
    writes.push({
      label: `room ${entry.key} (create)`,
      requests: entry.archived ? 2 : 1,
      apply: async () => {
        const made = await remote.createRoom(entry);
        // A `client_id` already used answers the room made with it, as it stands (Decision 9).
        if (made.kind !== entry.kind) {
          throw expected(
            `the key ${entry.key} already names room ${show(made.name)} (${made.room_id}, a ${made.kind} room) in this community: ` +
              'a key keeps naming its room. Give the new room another key, or set its kind to that room\'s.',
          );
        }
        const twin = files.rooms.find((other) => other !== entry && other.id === made.room_id);
        if (twin) {
          throw expected(
            `the key ${entry.key} already names room ${made.room_id}, which your files also list as ${twin.key}: remove one of the two entries.`,
          );
        }
        // The id and the server's rev are recorded at once, with the files' own name and archived
        // state kept: a push stopped before the reconcile below then reads the room as changed in
        // the files, and its rerun sends what this one did not.
        const wanted = { name: entry.name, archived: entry.archived };
        takeRoom(entry, made);
        Object.assign(entry, wanted);
        save();
        // Reconcile: the answer may be a room made by an earlier attempt, since renamed or archived,
        // and a new room is never created archived.
        const patch = roomPatch(entry, made);
        if (patch) {
          takeRoom(entry, await remote.updateRoom(made.room_id, patch));
          save();
        }
      },
    });
  }
  for (const room of plan.rooms) {
    if (room.kind !== 'existing' || room.state !== 'local') continue;
    const { entry, server } = room;
    const patch = roomPatch(entry, server);
    if (!patch) continue;
    writes.push({
      label: `room ${entry.key}`,
      requests: 1,
      apply: async () => {
        takeRoom(entry, await remote.updateRoom(server.room_id, patch));
        save();
      },
    });
  }
  return writes;
}

/** Revs to refresh without a write: both sides agree, or the files carried no base. */
function refreshConverged(files: CommunityFiles, plan: Plan): number {
  let refreshed = 0;
  if (plan.settings.state === 'converged') {
    files.settings.rev = plan.settings.serverRev;
    refreshed++;
  }
  if (plan.joinLink.state === 'converged') {
    files.join_link_rev = plan.joinLink.serverRev;
    refreshed++;
  }
  for (const room of plan.rooms) {
    if (room.kind === 'existing' && room.state === 'converged') {
      room.entry.rev = room.serverRev;
      room.entry.kind = room.server.kind;
      refreshed++;
    }
  }
  return refreshed;
}

function pushListing(plan: Plan): Listing {
  const listing: Listing = { changes: [], serverChanged: [], conflicts: [], leftAsIs: [], kept: [] };
  const { settings, joinLink } = plan;
  if (settings.state === 'local') {
    listing.changes.push(`~ settings: ${settingsChanges(settings.server, settings.file).join('; ')}`);
    if (settings.server.rules !== settings.file.rules) {
      listing.changes.push(...lineDiff(settings.server.rules, settings.file.rules).map((line) => `    ${line}`));
    }
  }
  if (joinLink.state === 'local') listing.changes.push(`~ join link: ${joinLinkChange(joinLink.server, joinLink.file)}`);
  for (const room of plan.rooms) {
    const { entry } = room;
    if (room.kind === 'create') {
      listing.changes.push(`+ room ${entry.key}: create ${show(entry.name)} (${entry.kind})${entry.archived ? ', archived' : ''}`);
    } else if (room.state === 'local') {
      listing.changes.push(`~ room ${entry.key}: ${roomChanges(roomContent(room.server), roomContent(entry)).join('; ')}`);
    }
  }
  const named = (state: SyncState) => [
    ...(settings.state === state ? ['settings'] : []),
    ...(joinLink.state === state ? ['join link'] : []),
    ...plan.rooms.flatMap((room) => (room.kind === 'existing' && room.state === state ? [`room ${room.entry.key}`] : [])),
  ];
  listing.serverChanged = named('remote');
  listing.conflicts = named('conflict');
  listing.leftAsIs = plan.untracked.map((room) => `room ${show(room.name)} (${room.room_id})`);
  return listing;
}

export async function pushCommunity(gitRootOrNull: string | null, selector: string | undefined, args: string[]): Promise<void> {
  const { autoConfirm, dryRun, theirs } = parseArgs(args);
  if (theirs) throw expected('`--theirs` is a pull option: `wayai pull communities/<folder> --theirs` takes the server\'s side of a conflict.');
  const gitRoot = requireGitRoot(gitRootOrNull);
  const target = resolveCommunityTarget(gitRoot, selector);
  if (target.folder === null) {
    throw expected(
      `No folder in ${communitiesDirLabel(gitRoot)}/ is community ${target.communityId}. Pull it first: wayai pull ${WAYAI_LAYOUT.communitiesSubdir}/${target.communityId}`,
    );
  }
  const { folder } = target;
  const files = readCommunityFolder(folder);
  if (!dryRun && !autoConfirm && !isInteractive()) {
    throw expected('Push changes the live community and asks first: pass -y when not running in a terminal, or --dry-run to only list the changes.');
  }
  const { organization_id: orgId } = requireRepoConfig(gitRoot);
  const label = path.basename(folder);
  assertInScope('communities', files.community_id, label);

  const remote = await openRemote(files.community_id);
  const server = await remote.read(orgId);
  const plan = planSync(files, server);
  console.log(`Community ${show(server.community.name)} (${server.community.community_id}) — ${path.relative(gitRoot, folder)}/`);

  const problems = pushProblems(files, server, plan);
  if (problems.length > 0) {
    throw expected(`Nothing was pushed:\n${problems.map((p) => `  - ${p}`).join('\n')}`);
  }
  const listing = pushListing(plan);
  printListing(listing);
  if (listing.conflicts.length > 0) {
    throw expected(`\nNothing was pushed: ${listing.conflicts.length} conflict(s). ${resolveConflicts(folder)}`);
  }

  const { communitiesDir } = resolveLayout(gitRoot);
  const save = () => {
    writeCommunityFolder(communitiesDir, folder, files);
  };
  const writes = pushWrites(files, plan, remote, save);
  if (writes.length === 0) {
    const refreshed = dryRun ? 0 : refreshConverged(files, plan);
    if (refreshed > 0) save();
    console.log(`\nNo changes to push${refreshed > 0 ? ` (${refreshed} rev(s) refreshed in ${COMMUNITY_FILE})` : ''}.`);
    if (!dryRun) seedScopeIfEmpty('communities', files.community_id);
    return;
  }

  const requests = writes.reduce((sum, write) => sum + write.requests, 0);
  console.log(
    `\nThis push makes ${requests} request(s) to the live community — ${paceDuration(requests)} at ${COMMUNITY_REQUESTS_PER_MINUTE} a minute.`,
  );
  if (dryRun) {
    console.log('Dry run — nothing was written.');
    return;
  }
  if (!autoConfirm) {
    if (!(await confirm('Apply these changes to the live community?'))) {
      console.log('Cancelled. Nothing was written.');
      return;
    }
    // The question can wait for minutes, and a web edit made meanwhile would be written over
    // with nothing left to show it. Read again: a change since the listing stops the push.
    const changed = changedSince(server, await remote.read(orgId));
    if (changed.length > 0) {
      throw expected(
        `The community changed while the question waited (${changed.join(', ')}). Nothing was written: push again to see the changes.`,
      );
    }
  }

  refreshConverged(files, plan);
  save();
  for (const [index, write] of writes.entries()) {
    try {
      await write.apply();
    } catch (err) {
      throw expected(
        `Push stopped at ${write.label}: ${stopReason(err)}\n` +
          `What it applied before stopping is on the community, and ${COMMUNITY_FILE} records it. Fix the cause and push again.`,
      );
    }
    console.log(`  [${index + 1}/${writes.length}] ${write.label}`);
  }
  console.log(
    `\nPushed ${writes.length} change(s). Commit ${path.relative(gitRoot, folder)}/: ${COMMUNITY_FILE} now records the ids and revs this push wrote.`,
  );
  seedScopeIfEmpty('communities', files.community_id);
}

/** The entities whose server state differs between two reads of one community. */
function changedSince(before: ServerState, after: ServerState): string[] {
  const id = before.community.community_id;
  const changed: string[] = [];
  if (serverSettings(id, before.settings).rev !== serverSettings(id, after.settings).rev) changed.push('settings');
  // The link too, not only its state: a link rotated meanwhile may already be shared.
  if (before.joinLink.enabled !== after.joinLink.enabled || before.joinLink.url !== after.joinLink.url) {
    changed.push('join link');
  }
  const revs = new Map(before.rooms.map((room) => [room.room_id, serverRoom(room).rev]));
  for (const room of after.rooms) {
    if (revs.get(room.room_id) !== serverRoom(room).rev) changed.push(`room ${show(room.name)}`);
  }
  return changed;
}

function stopReason(err: unknown): string {
  return sanitizeTerminalText(extractApiMessage(err));
}

// ── pull ──

/** Add the server's rooms the files do not list, each under a new stable key. */
function addUntracked(files: CommunityFiles, rooms: CommunityRoom[]): string[] {
  const taken = new Set(files.rooms.map((room) => room.key));
  return rooms.map((room) => {
    const key = localName(room.name, room.room_id, taken, 'room');
    taken.add(key);
    const entry: RoomEntry = { key, name: room.name, kind: room.kind, archived: room.archived };
    takeRoom(entry, room);
    files.rooms.push(entry);
    return `+ room ${key}: ${show(room.name)} (${room.kind})${room.archived ? ', archived' : ''}`;
  });
}

function filesFromServer(server: ServerState): CommunityFiles {
  const files: CommunityFiles = {
    community_id: server.community.community_id,
    name: server.community.name,
    settings: { slow_mode_seconds: 0, courses_label: '' },
    rules: '',
    join_link_enabled: false,
    rooms: [],
  };
  takeSettings(files, server.settings);
  takeJoinLink(files, server.joinLink.enabled);
  addUntracked(files, server.rooms);
  return files;
}

/** Apply the pull column of the Sync rules table to the files; returns what it changed and left. */
function pullMerge(files: CommunityFiles, server: ServerState, plan: Plan, theirs: boolean): Listing {
  const listing: Listing = { changes: [], serverChanged: [], conflicts: [], leftAsIs: [], kept: [] };
  const takes = (state: SyncState) => state === 'remote' || (state === 'conflict' && theirs);
  const note = (state: SyncState, what: string) => {
    if (state === 'local') listing.kept.push(what);
    if (state === 'conflict' && !theirs) listing.conflicts.push(what);
  };

  if (files.name !== server.community.name) {
    listing.changes.push(`~ name: ${show(files.name)} → ${show(server.community.name)}`);
    files.name = server.community.name;
  }
  const { settings, joinLink } = plan;
  if (takes(settings.state)) {
    listing.changes.push(`~ settings: ${settingsChanges(settings.file, settings.server).join('; ')}`);
    takeSettings(files, server.settings);
  }
  note(settings.state, 'settings');
  if (takes(joinLink.state)) {
    listing.changes.push(`~ join link: ${onOff(joinLink.file)} → ${onOff(joinLink.server)}`);
    takeJoinLink(files, server.joinLink.enabled);
  }
  note(joinLink.state, 'join link');
  for (const room of plan.rooms) {
    if (room.kind === 'create') {
      listing.kept.push(`room ${room.entry.key} (to create)`);
      continue;
    }
    const { entry, server: serverSide, state } = room;
    if (takes(state)) {
      listing.changes.push(`~ room ${entry.key}: ${roomChanges(roomContent(entry), roomContent(serverSide)).join('; ')}`);
      takeRoom(entry, serverSide);
    } else if (entry.kind !== serverSide.kind) {
      listing.changes.push(`~ room ${entry.key}: kind ${entry.kind} → ${serverSide.kind} (it never changes)`);
      entry.kind = serverSide.kind;
    }
    note(state, `room ${entry.key}`);
  }
  refreshConverged(files, plan);
  listing.changes.push(...addUntracked(files, plan.untracked));
  return listing;
}

export async function pullCommunity(gitRootOrNull: string | null, selector: string | undefined, args: string[]): Promise<void> {
  const { dryRun, theirs } = parseArgs(args);
  const gitRoot = requireGitRoot(gitRootOrNull);
  const target = resolveCommunityTarget(gitRoot, selector);
  // An existing folder's files are read and checked before any request; a first pull has none.
  let existing: { folder: string; files: CommunityFiles } | null = null;
  let communityId: string;
  if (target.folder === null) {
    communityId = target.communityId;
  } else {
    existing = { folder: target.folder, files: readCommunityFolder(target.folder) };
    communityId = existing.files.community_id;
  }
  const { organization_id: orgId } = requireRepoConfig(gitRoot);
  assertInScope('communities', communityId, existing ? path.basename(existing.folder) : undefined);

  const { communitiesDir } = resolveLayout(gitRoot);
  const remote = await openRemote(communityId);
  const server = await remote.read(orgId);

  if (!existing) {
    const taken = new Set(safeReaddir(communitiesDir));
    const folder = path.join(communitiesDir, localName(server.community.name, communityId, taken, 'community'));
    const files = filesFromServer(server);
    const where = `${path.relative(gitRoot, folder)}/`;
    if (dryRun) {
      console.log(`Dry run — would write community ${show(server.community.name)} to ${where} with ${files.rooms.length} room(s).`);
      return;
    }
    requireRealSubdirNoSymlink(gitRoot, communitiesDir, true);
    writeCommunityFolder(communitiesDir, folder, files);
    console.log(`Community ${show(server.community.name)} written to ${where} (${files.rooms.length} room(s)). Commit it: its ids and revs are the base the next push compares against.`);
    seedScopeIfEmpty('communities', communityId);
    return;
  }

  const { folder, files: local } = existing;
  const plan = planSync(local, server);
  const problems = unknownIdProblems(plan);
  if (problems.length > 0) {
    throw expected(`Nothing was pulled:\n${problems.map((p) => `  - ${p}`).join('\n')}`);
  }
  const listing = pullMerge(local, server, plan, theirs);
  console.log(`Community ${show(server.community.name)} (${communityId}) — ${path.relative(gitRoot, folder)}/`);
  printListing(listing);
  if (dryRun) {
    console.log('\nDry run — nothing was written.');
  } else {
    const written = writeCommunityFolder(communitiesDir, folder, local);
    console.log(written.length > 0 ? `\nWrote ${written.join(', ')}.` : '\nAlready up to date.');
    seedScopeIfEmpty('communities', communityId);
  }
  if (listing.conflicts.length > 0) {
    throw expected(`${listing.conflicts.length} conflict(s) left as they are in your files. ${resolveConflicts(folder)}`);
  }
}

/** How a conflict is resolved: the server's side is taken, and an edit to keep is made again on top of it. */
function resolveConflicts(folder: string): string {
  return (
    `To resolve, take the server's side — wayai pull ${WAYAI_LAYOUT.communitiesSubdir}/${path.basename(folder)} --theirs — ` +
    'then make again any edit of yours to keep, and push.'
  );
}

function safeReaddir(dir: string): string[] {
  try {
    return fs.readdirSync(dir);
  } catch {
    return [];
  }
}
